import {
  CART_LOAD_STATUS,
  CART_SAVE_STATUS,
  DEFAULT_CART_STATE,
  SUBMIT_STATUS,
} from './cartUtils'

export const markCartDirty = cart => ({
  ...cart,
  dirty: true,
  saveStatus:
    cart.saveStatus === CART_SAVE_STATUS.SAVING ? CART_SAVE_STATUS.SAVING : CART_SAVE_STATUS.IDLE,
})

/**
 * Apply catalog remap result to cart. Marks dirty only when ids changed or lines dropped.
 * @param {object} cart
 * @param {{ lines: object[], changed: boolean, needsPersist: boolean }} remapResult
 */
export const applyCartLinesAfterCatalogRemap = (cart, remapResult) => {
  if (!remapResult?.changed) return cart
  const next = {
    ...cart,
    lines: remapResult.lines,
  }
  return remapResult.needsPersist ? markCartDirty(next) : next
}

/**
 * True until cart is READY with a cartOwnerId, or while a submit is in flight.
 * Silent no-op is intentional (race guard); UI must gate via selectIsCartEditable.
 */
export const isCartMutationBlocked = state =>
  state.cart.loadStatus !== CART_LOAD_STATUS.READY ||
  state.cart.cartOwnerId == null ||
  state.submitStatus === SUBMIT_STATUS.SUBMITTING

/**
 * Guard + dirty flag for user-driven cart edits.
 * When blocked, returns the same state reference (silent no-op) — intentional until
 * loadStatus === READY and cartOwnerId is set, and while not submitting;
 * gate UI with selectIsCartEditable.
 * @param {object} state bulkOrder slice
 * @param {(cart: object) => object} cartUpdater
 */
export const withCartMutation = (state, cartUpdater) => {
  if (isCartMutationBlocked(state)) return state
  return {
    ...state,
    cart: markCartDirty(cartUpdater(state.cart)),
  }
}

export const applyCartFetchStart = (cart, viewerId) => {
  const ownerChanged = viewerId != null && viewerId !== cart.cartOwnerId
  const ownerCart = ownerChanged ? { ...DEFAULT_CART_STATE } : cart

  return {
    ...ownerCart,
    cartOwnerId: viewerId,
    loadStatus: CART_LOAD_STATUS.LOADING,
  }
}

/**
 * Replace cart from a server snapshot (fetch success or submit success).
 * @param {object} cart
 * @param {object} normalized from normalizeCartResponse
 */
export const replaceCartFromServer = (cart, normalized) => ({
  ...cart,
  lines: normalized.lines,
  reference: normalized.reference,
  remark: normalized.remark,
  cartId: normalized.cartId,
  serverRevision: normalized.serverRevision,
  submissionId: normalized.submissionId,
  dirty: false,
  loadStatus: CART_LOAD_STATUS.READY,
  saveStatus: CART_SAVE_STATUS.IDLE,
  hasCartSaveError: false,
})

export const applyCartFetchError = cart => ({
  ...cart,
  loadStatus: CART_LOAD_STATUS.FAILED,
})

export const applyCartSaveStart = cart => {
  if (cart.saveStatus === CART_SAVE_STATUS.SAVING) return cart

  return {
    ...cart,
    dirty: false,
    saveStatus: CART_SAVE_STATUS.SAVING,
  }
}

/**
 * @param {object} cart
 * @param {object} normalized from normalizeCartResponse
 */
export const applyCartSaveSuccess = (cart, normalized) => {
  // Edit during save: keep local lines/meta, adopt server revision for the next PUT.
  if (cart.dirty) {
    return {
      ...cart,
      cartId: normalized.cartId ?? cart.cartId,
      serverRevision: normalized.serverRevision ?? cart.serverRevision,
      submissionId: normalized.submissionId ?? cart.submissionId,
      saveStatus: CART_SAVE_STATUS.IDLE,
      hasCartSaveError: false,
    }
  }

  return {
    ...cart,
    lines: normalized.lines,
    reference: normalized.reference,
    remark: normalized.remark,
    cartId: normalized.cartId ?? cart.cartId,
    serverRevision: normalized.serverRevision ?? cart.serverRevision,
    submissionId: normalized.submissionId ?? cart.submissionId,
    saveStatus: CART_SAVE_STATUS.SAVED,
    hasCartSaveError: false,
  }
}

/** Non-conflict save failure: keep dirty so the user can retry. */
export const applyCartSaveError = cart => ({
  ...cart,
  dirty: true,
  saveStatus: CART_SAVE_STATUS.FAILED,
  hasCartSaveError: true,
})

/**
 * Conflict recovery: clear sticky error and leave fetch to replace cart.
 * Avoids flashing the save banner while GET runs.
 */
export const applyCartSaveConflict = cart => ({
  ...cart,
  saveStatus: CART_SAVE_STATUS.IDLE,
  hasCartSaveError: false,
})
